/* Bootstrap (imported in main.jsx, before this file) handles layout and
 * component structure throughout the app. This file re-skins it to match
 * the Bizmoto brand (2026-07-27 "professional look and feel" pass) without
 * a Sass rebuild - no build-tooling change, no new npm dependency, so this
 * carries zero install/build risk on top of what already works.
 *
 * Approach: Bootstrap 5.3 exposes most values as CSS custom properties, but
 * NOT all of them cascade from a single root override - component classes
 * like .btn-primary define their own local --bs-btn-* properties with
 * hardcoded values baked in at Sass-compile time, so overriding
 * :root { --bs-primary: ... } alone would recolor focus rings and a few
 * root-derived things, but leave button/badge/border backgrounds exactly
 * where they were. So below is two layers: root-level vars for the things
 * that DO cascade (focus rings, links, body font), plus explicit
 * component-level overrides (same selector + same custom property names
 * Bootstrap itself uses) for everything that doesn't. Loaded after
 * bootstrap.min.css, so these win on the cascade without needing
 * `!important` anywhere.
 *
 * Brand colors sampled directly from public/bizmo-logo.png (not eyeballed):
 * amber #FFBA00 (the icon), navy #0E4361 (the wordmark). Navy is used as
 * the interactive/primary color (buttons, links, focus rings) rather than
 * amber - amber-on-white fails contrast for text-sized UI elements, navy
 * reads as trustworthy/professional and is the conventional choice for a
 * fintech-adjacent product anyway. Amber becomes the "highlight/attention"
 * accent instead, mapped onto Bootstrap's existing warning color (already
 * a near-identical yellow/amber by default) so the one card that already
 * used `border-warning` for emphasis (see Dashboard.jsx's "Exhausted total"
 * stat) now uses the exact brand amber with zero JSX changes.
 *
 * 2026-07-27 follow-up: briefly swapped to the theme-factory skill's "Tech
 * Innovation" preset (electric blue/neon cyan/dark navbar), then reverted
 * back to these original Bizmoto brand colors per explicit request - kept
 * this file's structure/technique from that round-trip (same override
 * approach either way), just the color values are back to the brand's own.
 *
 * Deliberately NOT touched: --bs-success / --bs-danger. Recoloring "this
 * succeeded" / "this failed" away from conventional green/red would trade
 * brand consistency for a real usability regression - status color meaning
 * matters more here than brand purity.
 */

:root {
  --brand-navy: #0e4361;
  --brand-navy-dark: #092c40;
  --brand-navy-darker: #071f2d;
  --brand-amber: #ffba00;
  --brand-amber-dark: #e6a800;

  --bs-body-font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica,
    Arial, sans-serif;

  /* 15px instead of Bootstrap's 16px default (2026-08-20, user request:
   * "body content text a bit smaller"). Deliberately a font-size change
   * only, NOT a smaller root/html font size - that would have scaled every
   * rem-based padding, margin and border-radius in the app along with it,
   * making the whole UI denser when what was asked for was smaller text.
   * Everything sized relatively (.small at 0.875em, badges at 0.75em,
   * alerts, card bodies, paragraphs, list groups) follows this
   * automatically; the handful of Bootstrap components that bake in an
   * absolute 1rem/0.875rem at Sass-compile time instead are re-pointed at
   * it explicitly in the "Type scale" block below. */
  --bs-body-font-size: 0.9375rem;

  --bs-primary: var(--brand-navy);
  --bs-primary-rgb: 14, 67, 97;
  --bs-link-color: var(--brand-navy);
  --bs-link-color-rgb: 14, 67, 97;
  --bs-link-hover-color: var(--brand-navy-dark);
  --bs-link-hover-color-rgb: 9, 44, 64;

  --bs-warning: var(--brand-amber);
  --bs-warning-rgb: 255, 186, 0;

  --bs-border-radius: 0.5rem;
  --bs-border-radius-sm: 0.375rem;
  --bs-border-radius-lg: 0.75rem;
}

body {
  font-family: var(--bs-body-font-family);
  background-color: #f5f6f8;
  color: #212934;
}

h1, h2, h3, h4, h5, h6 {
  font-family: var(--bs-body-font-family);
  font-weight: 600;
  letter-spacing: -0.01em;
}

/* ---- Type scale ---- */

/* Companion to --bs-body-font-size above. These are the components whose
 * font-size Bootstrap 5.3 compiles in as an absolute rem rather than
 * deriving from --bs-body-font-size, so they would otherwise stay at the
 * old 16px/14px while everything around them shrank - which reads as
 * accidental inconsistency (a 16px input sitting in a 15px form) rather
 * than a smaller UI. Each keeps its original RATIO to body text: what was
 * 1rem becomes 0.9375rem, what was 0.875rem (the -sm variants) becomes
 * 0.8125rem.
 *
 * Not listed here because they already track body size or use em:
 * .badge (0.75em), .small/small (0.875em), .alert, .card, .list-group,
 * .modal-body, .nav-link (set explicitly in the navbar block below and
 * intentionally left at its own size - shrinking chrome wasn't the ask).
 * Headings keep Bootstrap's own scale too: the request was about body
 * content, and shrinking h1-h6 as well would flatten the page hierarchy
 * rather than just calm the text down. */
.btn {
  --bs-btn-font-size: 0.9375rem;
}

.btn-sm,
.btn-group-sm > .btn {
  --bs-btn-font-size: 0.8125rem;
}

.form-control,
.form-select {
  font-size: 0.9375rem;
}

.form-control-sm,
.form-select-sm {
  font-size: 0.8125rem;
}

.dropdown-menu {
  --bs-dropdown-font-size: 0.9375rem;
}

.app-shell {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

/* ---- Buttons ---- */

.btn {
  --bs-btn-border-radius: 0.5rem;
  --bs-btn-font-weight: 500;
}

.btn-primary {
  --bs-btn-bg: var(--brand-navy);
  --bs-btn-border-color: var(--brand-navy);
  --bs-btn-hover-bg: var(--brand-navy-dark);
  --bs-btn-hover-border-color: var(--brand-navy-dark);
  --bs-btn-active-bg: var(--brand-navy-darker);
  --bs-btn-active-border-color: var(--brand-navy-darker);
  --bs-btn-disabled-bg: var(--brand-navy);
  --bs-btn-disabled-border-color: var(--brand-navy);
  --bs-btn-focus-shadow-rgb: 14, 67, 97;
}

.btn-outline-primary {
  --bs-btn-color: var(--brand-navy);
  --bs-btn-border-color: var(--brand-navy);
  --bs-btn-hover-bg: var(--brand-navy);
  --bs-btn-hover-border-color: var(--brand-navy);
  --bs-btn-active-bg: var(--brand-navy-dark);
  --bs-btn-active-border-color: var(--brand-navy-dark);
  --bs-btn-focus-shadow-rgb: 14, 67, 97;
}

.btn-link {
  --bs-btn-color: var(--brand-navy);
  --bs-btn-hover-color: var(--brand-navy-dark);
}

/* ---- Forms ---- */

.form-control:focus,
.form-check-input:focus {
  border-color: var(--brand-navy);
  box-shadow: 0 0 0 0.25rem rgba(14, 67, 97, 0.15);
}

.form-check-input:checked {
  background-color: var(--brand-navy);
  border-color: var(--brand-navy);
}

.form-label {
  font-weight: 500;
  color: #495566;
}

/* ---- Cards ---- */

.card {
  --bs-card-border-radius: 0.75rem;
}

.shadow-sm {
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.06), 0 1px 2px rgba(16, 24, 40, 0.04) !important;
}

/* ---- Navbar ---- */

.navbar {
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.06);
}

.navbar-brand img {
  display: block;
}

.navbar-nav .nav-link {
  font-weight: 500;
  font-size: 0.875rem;
  color: #495566;
  border-radius: 0.375rem;
  padding-left: 0.75rem;
  padding-right: 0.75rem;
  transition: color 0.2s ease, background-color 0.2s ease, transform 0.2s ease;
}

.navbar-nav .nav-link:hover,
.navbar-nav .nav-link:focus {
  color: var(--brand-navy);
  background-color: rgba(14, 67, 97, 0.06);
  transform: translateY(-1px);
}

.navbar-nav .nav-link.active {
  color: #ffffff;
  font-weight: 600;
  background-color: var(--brand-amber);
}

.navbar-nav .nav-link.active:hover,
.navbar-nav .nav-link.active:focus {
  color: #ffffff;
  background-color: var(--brand-amber-dark);
}

@media (prefers-reduced-motion: reduce) {
  .navbar-nav .nav-link {
    transition: none;
  }

  .navbar-nav .nav-link:hover,
  .navbar-nav .nav-link:focus {
    transform: none;
  }
}

/* ---- Tables ---- */

/* Applies to every table on every page (Merchants, Transaction Pending,
 * Transaction Lookup, Error codes, Dashboard's volume table,
 * Retry-exhausted, PendingStatusModal) - a single global rule instead of
 * a `small` class added table-by-table in each page's JSX, so a future new
 * table automatically matches without needing to remember to add anything.
 * `thead th`'s own font-size below is an absolute rem value, not relative,
 * so it isn't affected by this.
 *
 * 2026-08-20: dropped 0.875rem -> 0.8125rem (14px -> 13px) alongside the
 * --bs-body-font-size change above, keeping tables their original one notch
 * below body text rather than letting them creep up to the same size. */
.table {
  font-size: 0.8125rem;
}

.table > thead {
  --bs-table-color: #6b7686;
}

.table thead th {
  font-size: 0.68rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  border-bottom-width: 1px;
}

/* ---- Badges ---- */

.badge {
  font-weight: 500;
  letter-spacing: 0.01em;
}

/* ---- Live indicators ---- */

/* Small pulsing amber dot marking a UI element as "this is a live/polled
 * feed, not a static snapshot" - first use is Transaction Monitoring's
 * "Live summary" tab label (2026-08-19). Amber (brand-amber) rather than
 * a conventional green "live" dot, to stay on-brand with this file's
 * existing amber/navy palette rather than introducing a third accent
 * color. `display: inline-block` + vertical-align keeps it sitting on the
 * text baseline like a status dot rather than a block. Respects
 * prefers-reduced-motion the same way the navbar hover transform above
 * does - shows as a plain static dot instead of animating. */
.live-pulse-dot {
  display: inline-block;
  width: 0.5rem;
  height: 0.5rem;
  margin-left: 0.15rem;
  vertical-align: middle;
  border-radius: 50%;
  background-color: var(--brand-amber);
  box-shadow: 0 0 0 rgba(255, 186, 0, 0.6);
  animation: live-pulse-dot-anim 1.6s ease-out infinite;
}

@keyframes live-pulse-dot-anim {
  0% {
    box-shadow: 0 0 0 0 rgba(255, 186, 0, 0.6);
  }
  70% {
    box-shadow: 0 0 0 0.4rem rgba(255, 186, 0, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(255, 186, 0, 0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .live-pulse-dot {
    animation: none;
  }
}

/* ---- Auth pages (Login / ForgotPassword / SetNewPassword) ---- */

.auth-page {
  background: radial-gradient(circle at top, #ffffff 0%, #eef1f5 55%, #e6eaf0 100%);
}

.auth-card {
  width: 380px;
  border-radius: 1rem !important;
  box-shadow: 0 4px 6px rgba(16, 24, 40, 0.05), 0 12px 24px rgba(16, 24, 40, 0.08) !important;
}

.auth-card .card-body {
  padding: 2.25rem 2rem !important;
}

.auth-accent-bar {
  height: 4px;
  border-radius: 1rem 1rem 0 0;
  background: linear-gradient(90deg, var(--brand-amber) 0%, var(--brand-navy) 100%);
}
